import React from 'react';
import { Star, Wine, GlassWater, Users, Clock, Music, Sparkles } from 'lucide-react';

// Use professional cocktail photos
const MARGARITA_IMAGE = "https://customer-assets.emergentagent.com/job_045563bf-4bdb-48a2-991f-697b1fca894b/artifacts/5rv2nke0_Mango%20Chilli.jpg";
const TEQUILA_IMAGE = "https://images.unsplash.com/photo-1675808414195-47a409dccde0?crop=entropy&cs=srgb&fm=jpg&w=800&h=600&fit=crop";
const BRUNCH_IMAGE = "https://customer-assets.emergentagent.com/job_045563bf-4bdb-48a2-991f-697b1fca894b/artifacts/hthspvyj_Chicharro%CC%81n.jpg";

const weeklyFiestas = [
  {
    id: 1,
    name: "Margarita Mondays",
    tagline: "A Curated Celebration",
    day: "EVERY MONDAY",
    time: "Evening",
    image: MARGARITA_IMAGE,
    color: "#F7B638",
    description: "Transform your Monday into a vibrant evening focused on our in-house margarita collection. Premium quality, tableside theatre, and seasonal flavors that create FOMO.",
    highlights: [
      "Signature Margarita Menu: Mango Chili, Guava Bravo, Classic Lime & more",
      "Tasting Flights: 3 small pours for the curious",
      "Pitcher Service: Perfect for groups of 4+",
      "Seasonal Specials: Limited-time flavors each month"
    ]
  },
  {
    id: 2,
    name: "Tequila Thursdays",
    tagline: "The Weekend Starts Here",
    day: "EVERY THURSDAY",
    time: "7 PM - 10 PM",
    image: TEQUILA_IMAGE,
    color: "#EE2535",
    description: "Casa Loco's destination for premium tequila experiences. High-energy evenings featuring live percussion, expert-guided spirit tastings, and exceptional food pairings.",
    highlights: [
      "Full Tequila Collection on display with history cards",
      "Live Percussion: Bongo & Tambo rhythms",
      "Agave Ritual Tables: Interactive premium tastings",
      "Curated Food Menu: Street tacos, ceviche, grilled seafood"
    ]
  },
  {
    id: 3,
    name: "Sunday Fiesta Brunch",
    tagline: "A Family Celebration",
    day: "EVERY SUNDAY",
    time: "11 AM - 1 PM",
    image: BRUNCH_IMAGE,
    color: "#51A262",
    description: "A relaxed, premium family brunch with interactive taco stations, live mariachi music, and curated menus. Pure Mexican authenticity with personal service.",
    highlights: [
      "Interactive Taco Building Station",
      "Guacamole Trolley: Made fresh at your table",
      "Live Mariachi Music",
      "Optional Sip & Paint on select Sundays"
    ]
  }
];

const PromotionsSection = () => {
  return (
    <section className="relative py-16 sm:py-20 paper-texture" data-testid="promotions-section">
      <div className="zigzag-divider-green absolute top-0 left-0 right-0" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Star size={10} fill="#EE2535" stroke="none" />
            <Sparkles size={28} style={{ color: '#EE2535' }} />
            <Star size={10} fill="#EE2535" stroke="none" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-3" style={{ color: '#1A1918', textShadow: '2px 2px 0 rgba(238,37,53,0.15)' }} data-testid="promotions-title">
            OUR WEEKLY FIESTAS
          </h2>
          <div className="zigzag-divider max-w-xs mx-auto mb-4" />
          <p className="font-mono text-[#4A4641] max-w-xl mx-auto text-sm">
            Three signature experiences that define Casa Loco — premium quality, authentic vibes, unforgettable moments
          </p>
        </div>

        {/* Three Weekly Fiestas */}
        <div className="space-y-8">
          {weeklyFiestas.map((fiesta, idx) => (
            <div 
              key={fiesta.id} 
              className={`overflow-hidden ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              style={{ border: '4px solid #1A1918', boxShadow: '6px 6px 0 #1A1918' }}
              data-testid={`fiesta-${fiesta.id}`}
            >
              <div className={`grid lg:grid-cols-2 gap-0 ${idx % 2 === 1 ? 'lg:[direction:rtl]' : ''}`}>
                {/* Image */}
                <div className="aspect-video lg:aspect-auto lg:min-h-[400px] overflow-hidden lg:[direction:ltr]">
                  <img 
                    src={fiesta.image} 
                    alt={fiesta.name} 
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Content */}
                <div 
                  className="p-6 sm:p-8 lg:p-10 flex flex-col justify-center lg:[direction:ltr]"
                  style={{ backgroundColor: fiesta.color }}
                >
                  {/* Day Badge */}
                  <div className="flex items-center gap-2 mb-3">
                    <span 
                      className="inline-flex items-center gap-2 font-mono text-xs font-bold tracking-[0.15em] px-3 py-1.5"
                      style={{ backgroundColor: '#1A1918', color: 'white' }}
                    >
                      <Clock size={12} />
                      {fiesta.day}
                    </span>
                    <span className="font-mono text-xs font-bold" style={{ color: '#1A1918' }}>
                      {fiesta.time}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 
                    className="font-display text-2xl sm:text-3xl lg:text-4xl text-white mb-1"
                    style={{ textShadow: '2px 2px 0 rgba(0,0,0,0.3)' }}
                  >
                    {fiesta.name.toUpperCase()}
                  </h3>
                  <p className="font-mono text-white/80 text-sm mb-4 italic">
                    "{fiesta.tagline}"
                  </p>

                  {/* Description */}
                  <p className="font-mono text-white/90 text-sm leading-relaxed mb-5">
                    {fiesta.description}
                  </p>

                  {/* Highlights */}
                  <div className="space-y-2">
                    {fiesta.highlights.map((highlight, hIdx) => (
                      <div key={hIdx} className="flex items-start gap-2">
                        <Star size={10} fill="white" stroke="none" className="mt-1.5 flex-shrink-0" />
                        <span className="font-mono text-white text-xs">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <p className="font-mono text-[#4A4641] text-sm mb-4">
            Ready to join the fiesta?
          </p>
          <a 
            href="/location" 
            className="inline-block px-8 py-4 font-display tracking-wider text-lg transition-transform hover:translate-x-[-2px] hover:translate-y-[-2px]"
            data-testid="fiesta-location-btn"
            style={{ backgroundColor: '#EE2535', color: 'white', border: '3px solid #1A1918', boxShadow: '4px 4px 0 #1A1918' }}
          >
            VISIT US
          </a>
        </div>
      </div>
    </section>
  );
};

export default PromotionsSection;
